import {
  checkOtp,
  googleLoginUrl,
  loginOtp,
  patchProfile,
} from "../../../../services/auth";
import React, { useEffect, useRef, useState } from "react";
import OTPInput from "react-otp-input";
import { useMutation } from "react-query";
import { useNavigate } from "react-router-dom";
import { useStep } from "usehooks-ts";
import * as Helpers from "../../../../utils/helpers";
import { useDispatch, useSelector } from "react-redux";
import { actions as AuthActions } from "../../../../store/authRedux/actions";
import { actions as CommonActions } from "../../../../store/commonRedux/actions";
import {
  Avatar,
  Button,
  Checkbox,
  Col,
  Form,
  Input,
  Row,
  Tooltip,
} from "antd";
import {
  LoginOutlined,
  EditOutlined,
  InfoCircleOutlined,
  MessageOutlined,
  MobileOutlined,
  PlusOutlined,
  UserOutlined,
} from "@ant-design/icons";
import styles from "./styles.module.css";
import * as Yup from "yup";
import { useFormik } from "formik";
import { DownloadAppButton, FieldError } from "../../../../components";
import { requestFcmToken, settleWithin } from "../../../../utils/Messaging";
import useLoginLanding from "../../useLoginLanding";
import GoogleIcon from "./GoogleIcon";

const FCM_TOKEN_WAIT_MS = 2000;

/* Seconds before «ارسال مجدد» becomes available again. The API allows three
 * code requests a minute per number (see the `otp-request` limiter), so a
 * minute's wait can never walk the user into a 429 — the button is only ever
 * enabled when the request behind it will actually go through. */
const RESEND_SECONDS = 60;

/* چرا این جمله لازم است: دکمه‌ی گوگل کنارِ «ورود یا ثبت‌نام» می‌نشیند و بدون
 * توضیح، همان وعده را می‌دهد. ولی هویت در تسکامون شماره‌ی همراه است — گوگل فقط
 * یک ایمیلِ از پیش ثبت‌شده را به حسابِ موجود وصل می‌کند. */
const GOOGLE_HINT =
  "ورود با گوگل فقط برای حساب‌هایی کار می‌کند که از قبل ساخته شده‌اند و ایمیلشان در پروفایل ثبت شده است. برای ثبت‌نام، با شماره همراه وارد شوید.";

const formatCountdown = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const rest = String(seconds % 60).padStart(2, "0");
  return Helpers.toFa(`${minutes}:${rest}`);
};

const StepIllustration = ({ step }) => {
  const icons = {
    1: <MobileOutlined />,
    2: <MessageOutlined />,
    3: <UserOutlined />,
  };

  return <span className={styles.stepIconGlyph}>{icons[step]}</span>;
};

const validationSchema1 = Yup.object({
  mobile: Yup.string()
    .min(10, "شماره همراه را به درستی وارد نمایید")
    .max(11, "شماره همراه را به درستی وارد نمایید")
    .required("وارد کردن این فیلد الزامی است"),
});

const validationSchema2 = Yup.object({
  token: Yup.string()
    ?.length(5, "کد تایید ۵ رقمی است")
    .required("وارد کردن این فیلد الزامی است"),
});

const validationSchema3 = Yup.object({
  first_name: Yup.string().required("وارد کردن این فیلد الزامی است"),
  last_name: Yup.string().required("وارد کردن این فیلد الزامی است"),
});

const initialValues = {
  mobile: undefined,
  token: "",
};

const LoginForm = (props) => {
  const navigate = useNavigate();

  const dispatch = useDispatch();

  const tempData = useSelector(({ common }) => common.tempData);

  const [step, { goToNextStep, goToPrevStep }] = useStep(3);

  const [preparingOtp, setPreparingOtp] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const fcmTokenPromise = useRef(null);

  /* One interval for the whole countdown, torn down when it reaches zero or the
   * step changes — a `setTimeout` per second would keep firing after the user
   * has already left the screen. */
  useEffect(() => {
    if (resendIn <= 0) return undefined;

    const timer = window.setInterval(
      () => setResendIn((value) => (value > 0 ? value - 1 : 0)),
      1000,
    );
    return () => window.clearInterval(timer);
  }, [resendIn > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (step == 2 && !fcmTokenPromise.current) {
      fcmTokenPromise.current = requestFcmToken();
    }
  }, [step]);

  const submitOtp = async (values) => {
    setPreparingOtp(true);
    const fcm_token = await settleWithin(
      fcmTokenPromise.current,
      FCM_TOKEN_WAIT_MS,
    );
    setPreparingOtp(false);

    checkOtpUser.mutate({
      body: Helpers.removeEmptyFString({
        ...values,
        token: Helpers.convertPerToEn(values.token),
        fcm_token,
      }),
    });
  };

  const onSubmit = (values) => {
    step == 1
      ? loginUser.mutate({
          body: Helpers.removeEmptyFString(values),
        })
      : step == 2
        ? submitOtp(values)
        : userMutate.mutate({
            body: {
              ...Helpers.removeEmpty({
                first_name: values.first_name,
                last_name: values.last_name,
              }),
            },
          });
  };

  /* مقصدِ بعد از ورود، مشترک با صفحه‌ی بازگشت از گوگل. */
  const { goToPanel, land } = useLoginLanding();

  const formik = useFormik({
    initialValues,
    validationSchema:
      step == 1
        ? validationSchema1
        : step == 2
          ? validationSchema2
          : validationSchema3,
    onSubmit,
    validateOnChange: false,
    validateOnBlur: true,
  });

  const loginUser = useMutation(loginOtp, {
    onSuccess: (res, variables) => {
      setResendIn(RESEND_SECONDS);
      /* A resend is the same request from the same screen: moving the step on
       * would drop the user out of the code they are half-way through typing. */
      if (!variables?.resend) goToNextStep();
    },
    onError: (error) => {},
  });

  const resendOtp = () => {
    if (resendIn > 0 || loginUser.isLoading) return;

    /* The code that is about to be replaced is no longer the one that works —
     * leaving it in the boxes would submit a stale token the moment the fifth
     * digit was already there. */
    formik.setFieldValue("token", "");
    checkOtpUser.reset();

    loginUser.mutate({
      body: { mobile: formik.values.mobile },
      resend: true,
    });
  };

  const checkOtpUser = useMutation(checkOtp, {
    onSuccess: (res, variables) => {
      const user = res?.data?.user;

      if (variables?.body?.fcm_token) {
        dispatch(AuthActions.setFcmToken(variables.body.fcm_token));
      }

      dispatch(CommonActions.setTempData({ tempData: res.data }));
      dispatch(AuthActions.setAllOrganizations(res.data.user.organizations));
      dispatch(AuthActions.setCustomerOf(res.data.customer_of || []));
      dispatch(AuthActions.login(res.data));

      if (user.first_name && user.last_name) {
        setTimeout(
          () =>
            land({
              organizations: user?.organizations,
              memberships: res.data.customer_of,
            }),
          500,
        );
      } else {
        goToNextStep();
      }
    },
    onError: (error) => {},
  });

  const userMutate = useMutation(patchProfile, {
    onSuccess: (res) => {
      const organizations = tempData?.user?.organizations || [];
      const user = { ...res.data.user, organizations };

      dispatch(AuthActions.login({ ...tempData, user }));
      dispatch(AuthActions.setUser(user));
      setTimeout(
        () => land({ organizations, memberships: tempData?.customer_of }),
        500,
      );
    },
    onError: (error) => {},
  });

  const formLoading =
    loginUser.isLoading ||
    preparingOtp ||
    checkOtpUser.isLoading ||
    userMutate.isLoading;

  useEffect(() => {
    if (formik.values.token?.length == 5) {
      formik.submitForm();
    }
  }, [formik.values.token]);

  return (
    <form
      className={styles.container}
      onSubmit={(e) => {
        e.preventDefault();
      }}
    >
      <div className={`d-flex align-items-center gap-2 ${styles.page_title}`}>
        <LoginOutlined size={24} />
        ورود به حساب کاربری
      </div>
      <div className={styles.formCard}>
        <div className={styles.stepIntro}>
          <div className={styles.step_ic}>
            <StepIllustration step={step} />
          </div>
          <span className={styles.stepCounter}>
            {step === 1
              ? "ورود یا ثبت‌نام"
              : step === 2
                ? "تأیید شماره همراه"
                : "تکمیل حساب کاربری"}
          </span>
          <p className={styles.step_title}>
            {step == 1
              ? "لطفا شماره همراه خود را وارد نمایید"
              : step == 2
                ? `لطفا کد ارسال شده به شماره همراه ${formik.values.mobile} را وارد نمایید`
                : "نام و نام خانوادگی خود را وارد کنید"}
          </p>
        </div>
        {step == 4 ? (
          <>
            <div
              key={"user-div-first"}
              className="w-100 mb-2"
              style={{
                maxWidth: 384,
              }}
            ></div>
            <div
              key={"user-div-last"}
              className="w-100 mb-5 gap-3"
              style={{
                maxWidth: 700,
              }}
            >
              <Row gutter={[8, 8]}>
                {tempData?.user?.organizations.map((org) => (
                  <Col xs={24} lg={12}>
                    <div className={`${styles.org_card} border`}>
                      <div className="d-flex align-items-center gap-2">
                        <Avatar size="large" src={org?.avatar?.original_url} />
                        <span className={styles.org_card_title}>
                          {org?.title}
                        </span>
                      </div>
                      <Button
                        icon={<LoginOutlined size={18} />}
                        onClick={() => goToPanel(org)}
                      >
                        ورود
                      </Button>
                    </div>
                  </Col>
                ))}
              </Row>
            </div>

            <Button
              type="link"
              icon={<PlusOutlined />}
              disabled={(tempData?.user?.organizations?.length || 0) >= 3}
              onClick={() => navigate("/register", { replace: true })}
            >
              {(tempData?.user?.organizations?.length || 0) >= 3
                ? "حداکثر ۳ سازمان"
                : "افزودن سازمان جدید"}
            </Button>
          </>
        ) : step == 3 ? (
          <>
            <span
              className="w-100 text-center text-primary font-semibold cursor-pointer mb-2"
              style={{ fontWeight: 600 }}
            >
              اطلاعات فردی خود را تکمیل کنید
            </span>
            <div
              key={"user-div-first"}
              className="w-100 mb-2"
              style={{
                maxWidth: 384,
              }}
            >
              <Form.Item
                label="نام"
                className="mb-1 w-100"
                labelCol={{ span: 24 }}
                required={true}
              >
                <Input
                  size="large"
                  placeholder="نام خود را وارد نمایید"
                  onChange={(e) =>
                    formik.setFieldValue("first_name", e.target.value)
                  }
                  onBlur={() => formik.setFieldTouched("first_name", true)}
                />
                <FieldError formik={formik} name="first_name" />
              </Form.Item>
            </div>
            <div
              key={"user-div-last"}
              className="w-100 mb-3"
              style={{
                maxWidth: 384,
              }}
            >
              <Form.Item
                label="نام خانوادگی"
                className="mb-2 w-100"
                labelCol={{ span: 24 }}
                required={true}
              >
                <Input
                  size="large"
                  placeholder="نام خانوادگی خود را وارد نمایید"
                  onChange={(e) =>
                    formik.setFieldValue("last_name", e.target.value)
                  }
                  onBlur={() => formik.setFieldTouched("last_name", true)}
                />
                <FieldError formik={formik} name="last_name" />
              </Form.Item>
            </div>
          </>
        ) : step == 2 ? (
          <>
            <span
              className={`w-100 text-center text-primary cursor-pointer d-flex justify-content-center align-items-center gap-2 ${styles.editMobile}`}
              style={{ fontSize: 14, fontWeight: 600 }}
              onClick={() => {
                formik.resetForm();
                goToPrevStep();
              }}
            >
              <EditOutlined
                style={{
                  fontSize: 16,
                  color: "var(--ds-accent)",
                }}
              />
              ویرایش شماره همراه
            </span>
            <div
              key={"token-div"}
              className={styles.otpWrap}
            >
              <OTPInput
                containerStyle={{
                  width: "100%",
                  justifyContent: "space-between",
                  flexDirection: "row-reverse",
                  gap: "10px",
                }}
                numInputs={5}
                value={formik.values.token}
                onChange={(val) => {
                  /* Editing after a rejected code clears the red boxes: the
                     error belongs to the code that was submitted, not to the
                     one being typed now. */
                  if (checkOtpUser.isError) checkOtpUser.reset();
                  formik.setFieldValue(
                    "token",
                    Helpers.convertPerToEn(val).replace(/[^\d]/g, ""),
                  );
                }}
                renderInput={(props, index) => (
                  <input
                    key={`otp-inpt-${index}`}
                    {...props}
                    className={`${styles.otpInput} ${
                      checkOtpUser.isError ? styles.otpInputError : ""
                    }`}
                    inputMode="numeric"
                    /* iOS offers the code from the SMS straight above the
                       keyboard on the strength of this one attribute. Android's
                       WebOTP needs the message itself to end in `@host #code`,
                       which is an sms.ir template change, not a change here. */
                    autoComplete="one-time-code"
                  />
                )}
                shouldAutoFocus={true}
                inputType="text"
              />
              <FieldError formik={formik} name="token" />

              {/* The screen used to end here: no way to ask for another code
                  and nothing saying whether waiting was worth it. */}
              <div className={styles.resendRow}>
                {resendIn > 0 ? (
                  <span className={styles.resendWait}>
                    ارسال مجدد کد تا {formatCountdown(resendIn)} دیگر
                  </span>
                ) : (
                  <Button
                    type="link"
                    size="small"
                    className={styles.resendButton}
                    loading={loginUser.isLoading}
                    onClick={resendOtp}
                  >
                    ارسال مجدد کد
                  </Button>
                )}
              </div>
            </div>
          </>
        ) : (
          <div
            key={"phone-div"}
            className="w-100 d-flex align-items-start"
            style={{
              maxWidth: 384,
            }}
          >
            <Form.Item
              label="شماره همراه"
              className="mb-2 w-100"
              labelCol={{ span: 24 }}
              required={true}
            >
              <Input
                size="large"
                placeholder="شماره همراه خود را وارد نمایید"
                onChange={(e) =>
                  formik.setFieldValue(
                    "mobile",
                    Helpers.convertPerToEn(e.target.value),
                  )
                }
                onBlur={() => formik.setFieldTouched("mobile", true)}
                onPressEnter={formik.submitForm}
                inputMode="numeric"
              />
              <FieldError formik={formik} name="mobile" />
            </Form.Item>
          </div>
        )}
        {step === 1 && (
          <div className="w-100 px-2" style={{ maxWidth: 384 }}>
            <Checkbox defaultChecked>
              <span className={styles.cr_txt}>
                ورود یا ثبت نام به منزله قبول{" "}
                <a
                  href="https://taskamoon.ir/privacy-policy/"
                  target="_blank"
                  rel="noreferrer"
                >
                  قوانین و مقررات
                </a>{" "}
                است
              </span>
            </Checkbox>
          </div>
        )}
        {step !== 4 && (
          <Button
            type="primary"
            size="large"
            block
            className={styles.submitButton}
            loading={formLoading}
            onClick={() => !formLoading && formik.submitForm()}
          >
            {step === 1
              ? "ورود به حساب کاربری"
              : step === 2
                ? "تأیید و ادامه"
                : "ذخیره و ادامه"}
          </Button>
        )}

        {/* فقط در قدم اول. بعد از ارسال کد، رفتن به گوگل یعنی دور انداختنِ کدی
            که همین حالا پیامک شده و کاربر منتظرش است. */}
        {step === 1 && (
          <>
            <div className={styles.orDivider}>
              <span>یا</span>
            </div>

            <div className={styles.googleRow}>
              <Button
                disabled={formLoading}
                className={styles.googleButton}
                icon={<GoogleIcon size={16} />}
                onClick={() => {
                  window.location.href = googleLoginUrl();
                }}
              >
                ورود با حساب گوگل
              </Button>

              <Tooltip title={GOOGLE_HINT}>
                <span
                  className={styles.googleHint}
                  role="button"
                  tabIndex={0}
                  aria-label={GOOGLE_HINT}
                >
                  <InfoCircleOutlined />
                </span>
              </Tooltip>
            </div>
          </>
        )}
      </div>
      <div className={styles.downloadWrap}>
        <DownloadAppButton />
      </div>
    </form>
  );
};

export default LoginForm;
